import { Component, OnInit, ViewChild, OnDestroy } from '@angular/core';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import {
  animate,
  state,
  style,
  transition,
  trigger,
} from '@angular/animations';
import {
  AbstractControl,
  FormArray,
  FormBuilder,
  FormGroup,
} from '@angular/forms';

import { Subscription } from 'rxjs';
import {
  DataService,
  DataServiceResponse,
  PamlabApiServiceService,
  SnackbarService,
  ScreenSizeService,
  RouteService,
} from '@/shared/services';
import {
  CourseComboReturn,
  CourseComboInfo,
} from '../../../../../server/types';
export interface CourseCombo {
  combo_count: string;
  combo_size: string;
  course_combo: string;
  pass_rate: number;
  updated_year: string;
}

export interface Row {
  [key: string]: string | number | undefined;
  no: number;
  course_1?: string;
  course_2?: string;
  course_3?: string;
  course_4?: string;
  course_5?: string;
  pass_rate: number;
}

@Component({
  selector: 'app-course-combinations-content',
  templateUrl: './course-combinations-content.component.html',
  styleUrls: ['./course-combinations-content.component.css'],
  animations: [
    trigger('detailExpand', [
      state('collapsed', style({ height: '0px', minHeight: '0' })),
      state('expanded', style({ height: '*' })),
      transition(
        'expanded <=> collapsed',
        animate('225ms cubic-bezier(0.4, 0.0, 0.2, 1)'),
      ),
    ]),
  ],
  standalone: false,
})
export class CourseCombinationsContentComponent implements OnInit, OnDestroy {
  public dataPending!: boolean;
  private subscription: Subscription = new Subscription();
  courseCombosData!: CourseComboReturn; // variable to save course combo data that is coming in from backend
  displayedColumns!: string[]; // column headers in table
  tableData!: MatTableDataSource<Row>;
  mobileView: boolean = false;
  courseFiltersForm!: FormGroup;
  selectedCourseNumber: string = '';
  selectedCourseName: string = '';

  constructor(
    private snackbarService: SnackbarService,
    private dataService: DataService,
    private screenSizeService: ScreenSizeService,
    private fb: FormBuilder,
    private routeService: RouteService,
  ) {}

  @ViewChild(MatSort) sort!: MatSort;

  ngOnInit(): void {
    this.subscription.add(
      this.dataService
        .getData()
        .subscribe((dataStream: DataServiceResponse) => {
          if (dataStream.data) {
            const data = dataStream.data as CourseComboReturn;
            this.courseCombosData = data;
            this.courseFiltersForm = this.fb.group({
              items: this.fb.array([
                ...this.getNumberOfCrsList().map((_item) =>
                  this.fb.group({
                    course: '',
                    searchVal: '',
                    filteredCourses: [],
                    originalFilteredCourses: [],
                  }),
                ),
              ]),
            });
            this.populateTableData();
            this.populateFilteredCoursesDropdowns();
          }

          if (dataStream.error) {
            this.tableData = new MatTableDataSource<Row>([]);
            this.snackbarService.open(dataStream.error.message, 'error');
          }
        }),
    );

    this.subscription.add(
      this.dataService.getPendingData().subscribe((pending) => {
        this.dataPending = pending;
      }),
    );

    this.subscription.add(
      this.screenSizeService.screenView$.subscribe((view) => {
        this.mobileView = view === 'mobile';
      }),
    );
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  get formItems() {
    return this.courseFiltersForm.get('items') as FormArray;
  }

  customFilterPredicate() {
    const filterPredicate = (dataRow: Row, regexStr: string): boolean => {
      const comboCourses: string[] = [];

      Object.entries(dataRow).forEach(([key, value]) => {
        if (key.includes('course')) {
          if (value === this.selectedCourseName) {
            dataRow[key] = dataRow[this.selectedCourseNumber];
            dataRow[this.selectedCourseNumber] = this.selectedCourseName;
          }
          comboCourses.push(value as string);
        }
      });

      // comboCourses.sort(this.sortComboCourses());
      const rowCoursesStr = comboCourses.join(' ').toLowerCase();
      const regex = new RegExp(regexStr);
      const matched = regex.test(rowCoursesStr);

      // Populate the available courses for empty filters
      if (matched) {
        // Get selected courses from the filters
        const selectedCourses: string[] = [];
        this.formItems.controls.forEach((formControl) => {
          const course = formControl.get('course')!.value;
          if (course) {
            selectedCourses.push(course);
          }
        });

        const unselectedCourses = comboCourses.filter(
          (course) => !selectedCourses.includes(course),
        );

        this.formItems.controls.forEach((formItem) => {
          const course = formItem.get('course')?.value;
          const courseList = formItem.get('filteredCourses')?.value as string[];
          const uniqueCourses = [
            ...new Set([...courseList, ...unselectedCourses]),
          ].sort(this.sortComboCourses());
          if (!course) {
            formItem.get('filteredCourses')?.patchValue([...uniqueCourses]);
            formItem
              .get('originalFilteredCourses')
              ?.patchValue([...uniqueCourses]);
          }
        });
      }

      return matched;
    };
    return filterPredicate;
  }

  getNumberOfCrsList() {
    return Array.from(
      { length: this.courseCombosData.combo_size || 0 },
      (_, i) => i,
    );
  }

  // global filter function to filter results
  applyFilter(
    formControl: AbstractControl | null,
    courseNumber: string /* As 'course_1' | 'course_2' | 'course_3' | 'course_4' | 'course_5'*/,
  ) {
    if (!formControl) return;

    const filterCrsList = formControl.get('originalFilteredCourses')
      ?.value as string[];
    const searchCrsVal = (
      formControl.get('course')?.value as string
    ).toLowerCase();

    // Filter dropdown when the user is typing
    if (
      !filterCrsList.map((crs) => crs.toLowerCase()).includes(searchCrsVal) &&
      searchCrsVal
    ) {
      formControl
        .get('filteredCourses')
        ?.patchValue(
          filterCrsList.filter((crs) =>
            crs.toLowerCase().includes(searchCrsVal),
          ),
        );

      return;
    }

    this.selectedCourseName = formControl.get('course')?.value as string;
    this.selectedCourseNumber = courseNumber;

    const searchCourses: string[] = [];
    this.formItems.controls.forEach((formItem) => {
      const course = formItem.get('course')?.value;
      if (course) {
        searchCourses.push(course);
      }
      formItem.get('filteredCourses')?.patchValue([]); // Reset filtered courses
      formItem.get('originalFilteredCourses')?.patchValue([]); // Reset filtered courses
    });

    // searchCourses.sort(this.sortComboCourses());
    if (!searchCourses.length) {
      this.populateFilteredCoursesDropdowns();
    }

    // Build a regex pattern that checks for each pattern followed by others
    let lookaheadParts = '';
    if (searchCourses.length === 1) {
      lookaheadParts = `(?=.*\\b${searchCourses.at(0)!.toLowerCase()}\\b)`;
    } else {
      lookaheadParts = searchCourses
        .map((p1, index) => {
          return searchCourses
            .slice(index + 1)
            .map(
              (p2) =>
                `(?=.*\\b${p1.toLowerCase()}\\b.*\\b${p2.toLowerCase()}\\b)`,
            )
            .join('');
        })
        .join('');
    }
    this.tableData.filter = lookaheadParts;
  }

  calculateFilterDropdownHeight(filteredCourses: string[] | null) {
    if (!filteredCourses) return '';

    if (filteredCourses.length < 5) {
      return `${filteredCourses.length * 50}px`;
    } else {
      return '250px';
    }
  }

  navigateToGlossaryOfTerms(termValue: string) {
    this.routeService.navigateNewTab('/glossary-of-terms', {
      term_value: termValue,
    });
  }

  private populateTableData() {
    const courseCols: string[] = [];

    // Course labels based on combo size
    for (let i = 1; i <= this.courseCombosData.combo_size; ++i) {
      courseCols.push(`course_${i}`);
    }

    // Construct display columns dynamically based the combo size
    this.displayedColumns = ['no', ...courseCols, 'pass_rate'];
    const rows = this.courseCombosData.course_combos.map((comboInfo, index) =>
      this.generateRow(comboInfo, index),
    );
    this.tableData = new MatTableDataSource<Row>(rows);
    this.tableData.filterPredicate = this.customFilterPredicate();
    this.tableData.sort = this.sort;
  }

  private generateRow(comboInfo: CourseComboInfo, index: number): Row {
    const course_combo: { [k: string]: string } = {};

    comboInfo.course_combo.forEach(({ course_code, course_name }, i) => {
      const key = `course_${i + 1}`;
      course_combo[key] = course_code + ': ' + course_name;
    });

    return {
      ...course_combo,
      no: index + 1,
      pass_rate: comboInfo.pass_rate,
    };
  }

  private populateFilteredCoursesDropdowns() {
    const courses = new Set<string>();

    this.courseCombosData.course_combos.forEach((crsCombo) => {
      crsCombo.course_combo.forEach((crs) => {
        courses.add(`${crs.course_code}: ${crs.course_name}`);
      });
    });

    this.getNumberOfCrsList().forEach((_index) => {
      this.formItems.controls.forEach((formItem) => {
        const sortedCourses = [...courses].sort(this.sortComboCourses());
        formItem.get('filteredCourses')!.patchValue(sortedCourses);
        formItem.get('originalFilteredCourses')!.patchValue(sortedCourses);
      });
    });
  }

  private sortComboCourses() {
    const selectedCourseLevels = this.courseCombosData.selected_course_levels;

    return (a: string, b: string) => {
      if (!selectedCourseLevels.includes('Any')) {
        const regexStr = `${selectedCourseLevels[0]}` + '[0-9]{3}';
        const regex = new RegExp(regexStr, 'g');
        const aMatches = (a.match(regex) || []).length;
        const bMatches = (b.match(regex) || []).length;

        return bMatches - aMatches;
      } else {
        return a.localeCompare(b);
      }
    };
  }
}
